import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../stores/auth.store';
import api from '../lib/api';

export default function Settings() {
  const { t } = useTranslation();
  const { user } = useAuthStore();

  // WhatsApp state
  const [waLinked, setWaLinked] = useState(false);
  const [waPhone, setWaPhone] = useState('');
  const [waLinkCode, setWaLinkCode] = useState('');
  const [waLoading, setWaLoading] = useState(false);

  // Google state
  const [googleConnected, setGoogleConnected] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  // Load statuses
  useEffect(() => {
    loadWhatsAppStatus();
    // Check if just connected Google (callback redirect)
    const params = new URLSearchParams(window.location.search);
    if (params.get('google') === 'connected') {
      setGoogleConnected(true);
      window.history.replaceState({}, '', '/settings');
    }
  }, []);

  const loadWhatsAppStatus = async () => {
    try {
      const res = await api.get('/gateway/whatsapp/status');
      setWaLinked(res.data.linked);
      setWaPhone(res.data.phoneNumber || '');
    } catch {
      // Not connected
    }
  };

  const generateWhatsAppCode = async () => {
    setWaLoading(true);
    try {
      const res = await api.post('/gateway/whatsapp/link');
      setWaLinkCode(res.data.code);
    } catch (error) {
      console.error('Failed to generate WhatsApp code:', error);
    } finally {
      setWaLoading(false);
    }
  };

  const whatsappLink = waLinkCode
    ? `https://wa.me/573102197789?text=${encodeURIComponent(`connect ${waLinkCode}`)}`
    : '';

  return (
    <div className="min-h-screen bg-slate-50 p-6">
      <div className="max-w-2xl mx-auto space-y-6">
        <h1 className="text-2xl font-bold text-slate-900">{t('settings.title', 'Settings')}</h1>

        {/* INTEGRATIONS */}
        <section className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-100">
            <h2 className="text-lg font-semibold text-slate-900">Integrations</h2>
            <p className="text-sm text-slate-500">Connect tools so your agent can take real actions</p>
          </div>

          {/* WhatsApp */}
          <div className="px-6 py-4 border-b border-slate-50 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center text-xl">💬</div>
              <div>
                <div className="font-medium text-slate-900">WhatsApp</div>
                {waLinked ? (
                  <div className="text-xs text-green-600 flex items-center gap-1">
                    <span className="w-1.5 h-1.5 bg-green-500 rounded-full" />
                    Connected — {waPhone}
                  </div>
                ) : (
                  <div className="text-xs text-slate-400">Not connected</div>
                )}
              </div>
            </div>
            {waLinked ? (
              <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">Connected</span>
            ) : waLinkCode ? (
              <div className="text-right">
                <div className="text-xs text-slate-500 mb-1">Send this to +57 310 219 7789:</div>
                <code className="text-sm font-mono font-bold bg-slate-100 px-2 py-1 rounded">connect {waLinkCode}</code>
                <div className="mt-1">
                  <a
                    href={whatsappLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-green-600 hover:text-green-700 underline"
                  >
                    Open WhatsApp →
                  </a>
                </div>
              </div>
            ) : (
              <button
                onClick={generateWhatsAppCode}
                disabled={waLoading}
                className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700 disabled:opacity-50 transition-colors"
              >
                {waLoading ? 'Generating...' : t('whatsapp.connect', 'Connect WhatsApp')}
              </button>
            )}
          </div>

          {/* Google */}
          <div className="px-6 py-4 border-b border-slate-50 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center text-xl">📧</div>
              <div>
                <div className="font-medium text-slate-900">Google (Gmail + Calendar)</div>
                {googleConnected ? (
                  <div className="text-xs text-green-600 flex items-center gap-1">
                    <span className="w-1.5 h-1.5 bg-green-500 rounded-full" />
                    Connected — Gmail + Calendar
                  </div>
                ) : (
                  <div className="text-xs text-slate-400">Send emails, manage calendar</div>
                )}
              </div>
            </div>
            {googleConnected ? (
              <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">Connected</span>
            ) : (
              <button
                onClick={async () => {
                  setGoogleLoading(true);
                  try {
                    const res = await api.get('/auth/google/connect');
                    window.location.href = res.data.url;
                  } catch {
                    console.error('Failed to get Google auth URL');
                  } finally {
                    setGoogleLoading(false);
                  }
                }}
                disabled={googleLoading}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50 transition-colors"
              >
                {googleLoading ? 'Connecting...' : 'Connect Google'}
              </button>
            )}
          </div>

          {/* Telegram */}
          <div className="px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-sky-100 flex items-center justify-center text-xl">✈️</div>
              <div>
                <div className="font-medium text-slate-900">Telegram</div>
                <div className="text-xs text-slate-400">Not connected</div>
              </div>
            </div>
            <button
              disabled
              className="px-4 py-2 bg-slate-200 text-slate-500 rounded-lg text-sm cursor-not-allowed"
            >
              Coming soon
            </button>
          </div>
        </section>

        {/* PREFERENCES */}
        <section className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-100">
            <h2 className="text-lg font-semibold text-slate-900">Preferences</h2>
          </div>

          <div className="px-6 py-4 space-y-4">
            {/* Language */}
            <div className="flex items-center justify-between">
              <label className="text-sm text-slate-700">{t('settings.language', 'Language')}</label>
              <select
                defaultValue={user?.locale || 'en'}
                onChange={(e) => {
                  // Update i18n and potentially save to backend
                  import('../i18n').then(i18n => {
                    i18n.default.changeLanguage(e.target.value);
                    localStorage.setItem('mundus-language', e.target.value);
                  });
                }}
                className="border border-slate-200 rounded-lg px-3 py-1.5 text-sm"
              >
                <option value="en">English</option>
                <option value="es">Español</option>
                <option value="pt-BR">Português</option>
                <option value="fr">Français</option>
              </select>
            </div>
          </div>
        </section>

        {/* ACCOUNT */}
        <section className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-100">
            <h2 className="text-lg font-semibold text-slate-900">Account</h2>
          </div>
          <div className="px-6 py-4">
            <div className="text-sm text-slate-600">
              <p><strong>Email:</strong> {user?.email}</p>
              <p><strong>Plan:</strong> {user?.subscriptionTier || 'Demo'}</p>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
